import { easeInOutQuint } from 'just-curves';
import { waitFor } from '../../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { Camera } from '../../canvas/Camera';
import { ConfettiCannon } from '../../canvas/ConfettiCannon';
import { clearSelection } from '../../canvas/commands/SelectionCommands';
import { Phys } from '../../canvas/entities/Phys';
import { ContentInjector } from '../../canvas/injection/ContentInjector';
import { kioskPlayRecording } from '../../canvas/kiosk/KioskCommands';
import { KioskController } from '../../canvas/kiosk/KioskController';
import { ParticipantManager } from '../../canvas/participants/ParticipantManager';
import { AlertManager } from '../../packages/alerts/AlertManager';
import { Graphic } from '../../packages/graphics/Graphic';
import { objectLibraryInsert } from '../../packages/objectLibrary/ObjectLibraryCommands';
import { Sticky } from '../../packages/paper/Sticky';
import { ReadyCheckController } from '../../packages/readyCheck/ReadyCheckController';
import { Text } from '../../packages/text/Text';
import { Vote } from '../../packages/voting/Vote';
import { PopupManager } from '../../popups/PopupManager';
import { TutorialGoal, createTutorialGoals } from './TutorialGoals';
import {
    deleteText,
    popInInstructions,
    waitForConfetti,
    waitForElementClick,
    waitForState,
    waitForUIElement,
    waitForUIElementValue
} from './TutorialHelpers';
import { tutorialPopup } from './TutorialPopup';
import {
    ConfettiInstructions2,
    EndInstructions,
    ExportInstructions,
    ModeInstructions,
    SessionInstructions,
    ShareInstructions,
    StartInstructions,
    VideoProp4,
    VotingInstructions
} from './TutorialPrefabs';
import { TutorialStep } from './TutorialStep';

const intro = TutorialStep('intro', async r => {

    const popups = r.call(PopupManager);

    await popups.invoke(tutorialPopup(
        'Welcome to Ludi!',
        `... I will be your guide.
        This interactive tutorial will teach you the basics of using Ludi.
        If you want to cancel at any point, you can't!  The ride has begun.`,
        'Let\'s go!'
    ));

});

const shareBoard = TutorialStep('shareBoard', async r => {

    const popups = r.call(PopupManager);
    const injector = r.call(ContentInjector);
    const dispatch = r.call(CommandDispatcher);
    const alerts = r.call(AlertManager);
    const camera = r.call(Camera);
    const ctrl = r.call(KioskController);
    const objects = r.call(ObjectManager);
    const api = r.call(ApiClient);

    await camera.animateToFrame({ left: 0, top: 0, width: 1, height: 1 });

    const templateId = (
        await api.exec(v2.templates.list())
    ).filter(g => g.label === 'Retrospectives')[0].items.filter(t => t.label === 'The Sailboat')[0].id;
    dispatch(objectLibraryInsert, [templateId, 'The Sailboat']);
    await waitFor(100);
    dispatch(clearSelection);

    await popups.invoke(tutorialPopup(
        'Getting Ready to Start a Session',
        [
            'Behind me is the virtual whiteboard where you and your team interact and collaborate.',
            'Let\'s use this retrospective template to learn the basics of hosting your first session.',
        ],
        'Let\'s go!'
    ));

    injector.injectNative(ShareInstructions);
    injector.injectNative(VideoProp4);
    dispatch(clearSelection);

    await popInInstructions(r);

    const bounds = Phys.from(objects.getAll(Text)).bounds().inflate(200);
    await camera.animateToFrame({ ...bounds, left: bounds.left - 300, top: bounds.top + 200 }, 2000, easeInOutQuint);

    const setGoal = createTutorialGoals(alerts, [
        TutorialGoal('OSM', 'Open the share menu'),
        TutorialGoal('JBA', 'Set \'can be joined\' to anyone'),
    ],
        [1, 8]);

    await waitForUIElement('#share-view');
    setGoal('OSM', true);

    await waitForUIElementValue('#join-access', 'anyone with the link');
    setGoal('JBA', true);

    for (let i = 0; i < 5; i++) {
        await ctrl.getOrCreateActor();
        await waitFor(500);
    };

    await popups.invoke(tutorialPopup(
        'Nice!',
        [
            'Let\'s assume you shared the board link and your team are now joining.',
            'You can track who joins via the participant list on the top right of your screen.',
        ],
        'Okay'
    ));

    await waitFor(100);

});

const startMeeting = TutorialStep('startMeeting', async r => {

    const popups = r.call(PopupManager);
    const injector = r.call(ContentInjector);
    const dispatch = r.call(CommandDispatcher);
    const alerts = r.call(AlertManager);
    const objects = r.call(ObjectManager);
    const ptpts = r.call(ParticipantManager);

    deleteText(objects, [
        'Sharing and Options',
        'With your session structure ready,'
    ]);

    injector.injectNative(StartInstructions);
    dispatch(clearSelection);

    await popInInstructions(r);

    await popups.invoke(tutorialPopup(
        'Let\'s Get Started!',
        'Now that everyone is here and ready to go, let\'s look at how to start and manage your session.',
        'Got it!'
    ));

    const setGoal = createTutorialGoals(alerts, [
        TutorialGoal('LL', 'Lock your layout'),
        TutorialGoal('SM', 'Start the meeting'),
    ],
        [2, 8]);

    await waitForElementClick('#lock-layout');
    setGoal('LL', true);

    await waitForElementClick('#start-meeting');
    setGoal('SM', true);

    const recordings = [
        'HostGoal1',
        'HostRisk1',
        'HostAnchor1',
        'HostWind1',
        'HostWind2',
    ];

    const grahicId = Array.from(objects.getAll(Graphic)).map((g) => g.id)[0];
    const ids = (ptpts.toArray().map(p => p.id).filter(i => i !== ptpts.getSelf().id));

    for (let i = 0; i < 5; i++) {
        dispatch(kioskPlayRecording, [recordings[i], { '$USER': ids[i], '$ZONE': grahicId }]);
    }

    await waitFor(100);

});

const addReadyCheck = TutorialStep('addReadyCheck', async r => {

    const popups = r.call(PopupManager);
    const injector = r.call(ContentInjector);
    const dispatch = r.call(CommandDispatcher);
    const alerts = r.call(AlertManager);
    const objects = r.call(ObjectManager);
    const ptpts = r.call(ParticipantManager);
    const ctrl = r.call(ReadyCheckController);

    deleteText(objects, [
        'Start your Session',
        'To get started and reveal additional functionality'
    ]);

    injector.injectNative(SessionInstructions);
    dispatch(clearSelection);

    await popInInstructions(r);

    await popups.invoke(tutorialPopup(
        'And We\'re Off!',
        'Now that the meeting has started, let\'s look at the features available for managing the session and keeping your team engaged.',
        'Okay'
    ));

    const setGoal = createTutorialGoals(alerts, [
        TutorialGoal('TRC', 'Turn on ready check'),
        TutorialGoal('AT', 'Add a timer'),
    ],
        [3, 8]);

    await Promise.all([
        waitForElementClick('#ready-check').then(() => setGoal('TRC', true)),
        waitForElementClick('#meeting-timer').then(() => setGoal('AT', true)),
    ]);

    const ids = ptpts.map(p => p.id);
    for (const id of ids) {
        if (id !== ptpts.getSelf().id) {
            await waitFor(1000);
            ctrl.respondWith(true, id);
        }
    }

    await waitFor(100);

});

const votingRound = TutorialStep('votingRound', async r => {

    const popups = r.call(PopupManager);
    const injector = r.call(ContentInjector);
    const dispatch = r.call(CommandDispatcher);
    const alerts = r.call(AlertManager);
    const objects = r.call(ObjectManager);

    deleteText(objects, [
        'Session Management:',
        'Click \'Meeting in Progress\' in the top bar'
    ]);

    injector.injectNative(VotingInstructions);
    dispatch(clearSelection);

    await popInInstructions(r);

    await popups.invoke(tutorialPopup(
        'Voting',
        [
            'While your \'team\' is busy adding their thoughts, let\'s have a look at voting rounds.',
            'A great way to get everyone involved and gauge how they feel about certain topics.',
        ],
        'Let\'s vote!'
    ));

    const setGoal = createTutorialGoals(alerts, [
        TutorialGoal('SV', 'Start voting round'),
        TutorialGoal('VS', 'Vote on a sticky note'),
    ],
        [4, 8]);

    await waitForElementClick('#voting-round');
    setGoal('SV', true);

    await waitForState(objects, () => {
        const stickies = Array.from(objects.getAll(Sticky));
        const votes = stickies.map(s => s.children.filter(c => c instanceof Vote)).flat();

        return votes.length > 0;
    });
    setGoal('VS', true);

    await waitFor(100);

});

const designMode = TutorialStep('designMode', async r => {

    const popups = r.call(PopupManager);
    const injector = r.call(ContentInjector);
    const dispatch = r.call(CommandDispatcher);
    const alerts = r.call(AlertManager);
    const objects = r.call(ObjectManager);

    deleteText(objects, [
        'Voting Rounds:',
        'To start a round, select the +1 icon from the left menu'
    ]);

    injector.injectNative(ModeInstructions);
    dispatch(clearSelection);

    await popInInstructions(r);

    const setGoal = createTutorialGoals(alerts, [
        TutorialGoal('DM', 'Switch to design mode'),
    ],
        [5, 8]);

    await waitForElementClick('#design-mode');
    setGoal('DM', true);

    await waitFor(100);

});

const confetti = TutorialStep('wearHat', async r => {

    const popups = r.call(PopupManager);
    const alerts = r.call(AlertManager);
    const objects = r.call(ObjectManager);
    const injector = r.call(ContentInjector);
    const dispatch = r.call(CommandDispatcher);
    const cannon = r.call(ConfettiCannon);

    deleteText(objects, [
        'Mode Switching:',
        'While a meeting is in progress you might want'
    ]);

    injector.injectNative(ConfettiInstructions2);
    dispatch(clearSelection);

    await popInInstructions(r);

    await popups.invoke(tutorialPopup(
        'Amazing!',
        [
            'That covers quite a few of the basic tools available to facilitators running a session.',
            'Now for the big finish!',
        ],
        'Gotcha!'
    ));

    const setGoal = createTutorialGoals(alerts, [
        TutorialGoal('FC', 'Celebrate with confetti'),
    ],
        [6, 8]);

    await waitForConfetti(cannon);
    setGoal('FC', true);

    await waitFor(100);

});

const endMeeting = TutorialStep('startMeeting', async r => {

    const popups = r.call(PopupManager);
    const injector = r.call(ContentInjector);
    const dispatch = r.call(CommandDispatcher);
    const alerts = r.call(AlertManager);
    const objects = r.call(ObjectManager);

    deleteText(objects, [
        'Celebrate in style:',
        'When all activities are done, why not celebrate'
    ]);

    injector.injectNative(EndInstructions);
    dispatch(clearSelection);

    await popInInstructions(r);

    const setGoal = createTutorialGoals(alerts, [
        TutorialGoal('EM', 'End the meeting'),
    ],
        [7, 8]);

    await waitForElementClick('#end-meeting');
    setGoal('EM', true);

    await waitFor(100);

});

const exportBoard = TutorialStep('exportBoard', async r => {

    const popups = r.call(PopupManager);
    const injector = r.call(ContentInjector);
    const dispatch = r.call(CommandDispatcher);
    const alerts = r.call(AlertManager);
    const objects = r.call(ObjectManager);

    deleteText(objects, [
        'Well that was fun!',
        'To end the meeting for all participants click'
    ]);

    injector.injectNative(ExportInstructions);
    dispatch(clearSelection);

    await popInInstructions(r);

    const setGoal = createTutorialGoals(alerts, [
        TutorialGoal('OE', 'Open the export options'),
    ],
        [8, 8]);

    await waitForUIElement('#export-data');
    setGoal('OE', true);

    await waitFor(100);

});

export const HostTutorial = [
    // intro,
    shareBoard,
    startMeeting,
    addReadyCheck,
    votingRound,
    designMode,
    confetti,
    endMeeting,
    exportBoard,
];

// await waitForState(objects, () => objects.get(frame.id).addon(ActivityFrameAddon).hidden === true);
